import React from 'react';

interface TrendPoint {
  label: string;
  spend: number;
  outflow: number;
}

interface EnterpriseSpendChartProps {
  title: string;
  subtitle: string;
  points: TrendPoint[];
  currencyCode?: string;
}

const EnterpriseSpendChart: React.FC<EnterpriseSpendChartProps> = ({
  title,
  subtitle,
  points,
  currencyCode = 'NGN',
}) => {
  const maxValue = Math.max(
    1,
    ...points.map((point) => Math.max(point.spend, point.outflow))
  );

  const totalSpend = points.reduce((sum, point) => sum + point.spend, 0);
  const totalOutflow = points.reduce((sum, point) => sum + point.outflow, 0);

  const formatAmount = (value: number) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      maximumFractionDigits: 0,
    }).format(value);

  return (
    <section className="enterprise-chart-card">
      <div className="enterprise-chart-card__header">
        <div>
          <h3>{title}</h3>
          <p>{subtitle}</p>
        </div>
        <div className="enterprise-chart-card__legend">
          <span>
            <i className="enterprise-chart-card__legend-dot enterprise-chart-card__legend-dot--spend" />
            Submitted Spend
          </span>
          <span>
            <i className="enterprise-chart-card__legend-dot enterprise-chart-card__legend-dot--outflow" />
            Wallet Outflow
          </span>
        </div>
      </div>

      <div className="enterprise-chart">
        {points.map((point) => (
          <div key={point.label} className="enterprise-chart__group">
            <div className="enterprise-chart__bars">
              <div
                className="enterprise-chart__bar enterprise-chart__bar--spend"
                style={{ height: `${(point.spend / maxValue) * 100}%` }}
                title={`${point.label} submitted: ${formatAmount(point.spend)}`}
              />
              <div
                className="enterprise-chart__bar enterprise-chart__bar--outflow"
                style={{ height: `${(point.outflow / maxValue) * 100}%` }}
                title={`${point.label} outflow: ${formatAmount(point.outflow)}`}
              />
            </div>
            <span className="enterprise-chart__label">{point.label}</span>
          </div>
        ))}
      </div>

      <div className="enterprise-chart-card__totals">
        <div>
          <label>Total Submitted</label>
          <strong>{formatAmount(totalSpend)}</strong>
        </div>
        <div>
          <label>Total Outflow</label>
          <strong>{formatAmount(totalOutflow)}</strong>
        </div>
      </div>
    </section>
  );
};

export default EnterpriseSpendChart;
